// Phase 4 — Cricket V3 flyer. Clean, professional design for cricket
// squads only. Layout: header → main (player list | logo + info)
// → clock band. Pure render; every parameter comes in via props
// from the Squads view.

import React from "react";
import { Star, Shield, CalendarDays, MapPin, Clock } from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import {
  playerSkillCodes, TeamCrest, teamLogoSrc,
  formatDateTemplate, formatTimeTemplate
} from "./flyerHelpers";
import { useVersion } from "../Version";
import { CricketFlyerMobile } from "./FlyerCricketMobile";
import { PittGabruzCricketFlyer } from "./FlyerPittGabruzCricket";

// Inline cricket-bat icon — angled willow blade + handle. Stroke
// width is fattened (2.4) and the blade gets a fill so the glyph
// reads cleanly at small flyer sizes. Stroke colour comes from
// `currentColor` so the parent chip can override.
function BatIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      {/* willow blade — filled with currentColor at low opacity for body */}
      <path d="M14.5 2.5l7 7-9 9-7-7 9-9z" fill="currentColor" fillOpacity="0.25"/>
      {/* handle */}
      <path d="M5.5 11.5l-3 3 4 4 3-3"/>
      {/* twine / shoulder marking */}
      <path d="M14.5 2.5l3-1 1 3"/>
    </svg>
  );
}
// Inline cricket-ball icon — solid red leather ball with twin
// equator-line seams. Renders bright against any background; the
// seams use currentColor so they pick up the chip's accent.
function BallIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      {/* leather body — bright crimson, like an actual match ball */}
      <circle cx="12" cy="12" r="8.5" fill="#dc2626" stroke="#7f1d1d"/>
      {/* highlight glint top-left */}
      <ellipse cx="9" cy="8.5" rx="2.2" ry="1.2" fill="#fca5a5" opacity="0.55"/>
      {/* twin seam lines, off-white */}
      <path d="M4.5 10c4.5 1.2 10.5 1.2 15 0" stroke="#fef3c7" strokeWidth="1.3"/>
      <path d="M4.5 14c4.5-1.2 10.5-1.2 15 0" stroke="#fef3c7" strokeWidth="1.3"/>
    </svg>
  );
}
// Maps a player's skill codes to a single "primary role" icon for
// the flyer's right-aligned column.
//   ALL category    → both bat + ball (all-rounder)
//   BAT category    → bat
//   BOWL_PACE/SPIN  → ball
//   WK              → nothing (handled by the "(wk)" prefix)
//   nothing on file → no icon (silent fallback)
function roleIconsFor(codes) {
  const cats = new Set((codes || []).map(c => SKILL_BY_CODE[c]?.category).filter(Boolean));
  const isBat   = cats.has("BAT")        || cats.has("ALL");
  const isBowl  = cats.has("BOWL_PACE")  || cats.has("BOWL_SPIN") || cats.has("ALL");
  return { isBat, isBowl };
}

export function CricketFlyerV3(props) {
  // Pitt Gabruz Cricket gets a bespoke portrait flyer (matches the
  // brand reference) used on both desktop and mobile.
  if (props.team?.code === "PITT_GABRUZ_CRICKET") {
    return <PittGabruzCricketFlyer {...props}/>;
  }
  // V4 mobile gets a dedicated flyer design — fresh layout, not a
  // patched-up version of the desktop V3 broadcast graphic.
  const { version } = useVersion();
  if (version === "v4") return <CricketFlyerMobile {...props}/>;
  return <CricketFlyerV3Desktop {...props}/>;
}

function CricketFlyerV3Desktop({
  match, opponent, opp, homeTeamName, homeAcronym, footerBrand,
  team, rules, startingPlayers, reportingTime,
  venueShort, venueAddress, captainMsg,
  showVolunteers, snacksVolunteer, alcoholVolunteer,
  captainPickId = null, wicketKeeperId = null,
  template = null
}) {
  const total = rules.startingSize;
  // Pitt Gabruz Cricket uses a compact layout — reporting-time band
  // sits inline under volunteers (right column) so the whole flyer
  // fits on one screen without scroll. Other cricket teams keep the
  // wide bottom band.
  const isPittGabruzCricket = team?.code === "PITT_GABRUZ_CRICKET";
  // Default league subheading varies by team — Rowdys plays in the
  // "Pittsburgh Cricket League"; everyone else (Pitt Gabruz, fallback)
  // plays in the "Pittsburgh Premier Cricket League".
  const defaultLeagueLine = team?.code === "ROWDYS_CRICKET"
    ? "PITTSBURGH CRICKET LEAGUE"
    : "PITTSBURGH PREMIER CRICKET LEAGUE";
  const leagueLine    = (template?.leagueLine   || defaultLeagueLine);
  const squadTitle    = (template?.squadTitle   || "PLAYING SQUAD");
  const primaryColor  = (template?.primaryColor || null);
  const bgTone        = (template?.bgTone       || null);
  const finalFooter   = (template?.footerBrand  || footerBrand);
  const useVolunteers = showVolunteers && (template?.showVolunteers !== false);
  const showSkills    = template?.showSkills !== false;
  const titleParts = squadTitle.trim().split(/\s+/);
  const titleA = titleParts[0];
  const titleB = titleParts.slice(1).join(" ");
  const rootStyle = {};
  if (primaryColor) rootStyle["--cv-gold"] = primaryColor;
  if (bgTone === "darker")  rootStyle["--cv-bg-0"] = "#04060e";
  if (bgTone === "lighter") rootStyle["--cv-bg-0"] = "#121b34";
  return (
    <div
      className={"poster poster--cricketV3" + (isPittGabruzCricket ? " poster--cricketV3--pittG" : "")}
      style={rootStyle}
    >

      <svg className="cv3-splatter cv3-splatter--tl" viewBox="0 0 200 200" aria-hidden="true">
        <g fill="currentColor" opacity="0.7">
          <path d="M0,0 L60,8 L42,28 L74,40 L18,56 L48,76 L4,76 L24,116 L0,98 Z"/>
          <circle cx="78" cy="22" r="3"/>
          <circle cx="58" cy="62" r="2"/>
          <circle cx="92" cy="48" r="2.5"/>
          <circle cx="40" cy="100" r="2"/>
        </g>
      </svg>
      <svg className="cv3-splatter cv3-splatter--br" viewBox="0 0 200 200" aria-hidden="true">
        <g fill="currentColor" opacity="0.7">
          <path d="M200,200 L140,192 L158,172 L126,160 L182,144 L152,124 L196,124 L176,84 L200,102 Z"/>
          <circle cx="122" cy="178" r="3"/>
          <circle cx="142" cy="138" r="2"/>
          <circle cx="108" cy="152" r="2.5"/>
          <circle cx="160" cy="100" r="2"/>
        </g>
      </svg>

      <header className="cv3-header">
        <div className="cv3-stars">
          <Star size={11} fill="currentColor" stroke="none"/>
          <Star size={15} fill="currentColor" stroke="none"/>
          <Star size={11} fill="currentColor" stroke="none"/>
        </div>
        <h1 className="cv3-brandTitle">GABRUZ UNIVERSE</h1>
        <div className="cv3-leaguePill">
          <span className="cv3-leagueChev">»</span>
          {leagueLine}
          <span className="cv3-leagueChev">«</span>
        </div>
      </header>

      <div className="cv3-main">
        <div className="cv3-left">
          <div className="cv3-bigTitle">
            <div className="cv3-bigTitle-a">{titleA}</div>
            {titleB && <div className="cv3-bigTitle-b">{titleB}</div>}
          </div>


          <div className="cv3-listPanel">
            <ol className="cv3-list">
              {startingPlayers.map((p, i) => {
                const codes = playerSkillCodes(team, p.id);
                const isCaptain = !!p.id && p.id === captainPickId;
                const isKeeper  = !!p.id && p.id === wicketKeeperId;
                const { isBat, isBowl } = roleIconsFor(codes);
                // Pitt Gabruz Cricket prefers the (c)/(wk)/bat/ball
                // treatment over the skill-chip list. Other cricket
                // teams still see the original skill chips.
                if (isPittGabruzCricket) {
                  const rowMod = [
                    "cv3-listRow",
                    "cv3-listRow--pittG",
                    isCaptain && "cv3-listRow--pittG-captain",
                    isKeeper  && "cv3-listRow--pittG-keeper"
                  ].filter(Boolean).join(" ");
                  return (
                    <li key={p.id || i} className={rowMod}>
                      <span className="cv3-listNum">{i + 1}</span>
                      <span className="cv3-listName">
                        {p.name}
                        {isCaptain && <em className="cv3-listTag cv3-listTag--c">C</em>}
                        {isKeeper  && <em className="cv3-listTag cv3-listTag--wk">WK</em>}
                      </span>
                      <span className="cv3-listRole" aria-hidden="true">
                        {isBat  && <span className="cv3-listRole-ic cv3-listRole-ic--bat"  title="Batter"><BatIcon  size={16}/></span>}
                        {isBowl && <span className="cv3-listRole-ic cv3-listRole-ic--ball" title="Bowler"><BallIcon size={16}/></span>}
                      </span>
                    </li>
                  );
                }
                return (
                  <li key={p.id || i}>
                    <span className="cv3-listNum">{i + 1}.</span>
                    <div className="cv3-listCol">
                      <span className="cv3-listName">
                        {p.name}
                        {isCaptain && <em className="cv3-listTag cv3-listTag--c"> (c)</em>}
                        {isKeeper  && <em className="cv3-listTag cv3-listTag--wk"> (wk)</em>}
                      </span>
                      {showSkills && codes.length > 0 && (
                        <span className="cv3-listSkills">
                          {codes.slice(0, 3).map(c => {
                            const skill = SKILL_BY_CODE[c];
                            return (
                              <em
                                key={c}
                                className={`cv3-listSkill cv3-listSkill--${skill?.category || "OTHER"}`}
                                title={skill?.label}
                              >{skill?.short || c}</em>
                            );
                          })}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="cv3-right">
          <div className="cv3-logoWrap">
            <TeamCrest team={team} fallbackName={homeAcronym} className="cv3-bigLogo" />
          </div>

          <div className="cv3-infoCard">
            <div className="cv3-infoRow">
              <div className="cv3-infoIcon cv3-infoIcon--opp">
                <Shield size={22}/>
              </div>
              <div className="cv3-infoText">
                <small>OPPONENT:</small>
                <b>{opp}</b>
              </div>
            </div>
            <div className="cv3-infoRow cv3-infoRow--dateTime">
              <div className="cv3-infoIcon">
                <CalendarDays size={22}/>
              </div>
              <div className="cv3-infoText">
                <small>DATE &amp; REPORTING TIME:</small>
                <b>
                  {formatDateTemplate(match?.date)}
                  {" · "}
                  {formatTimeTemplate(reportingTime)}
                </b>
              </div>
            </div>
            <div className="cv3-infoRow">
              <div className="cv3-infoIcon">
                <MapPin size={22}/>
              </div>
              <div className="cv3-infoText">
                <small>GROUND:</small>
                <b>{venueShort}</b>
                {venueAddress && <em>{venueAddress}</em>}
              </div>
            </div>
          </div>

          {/* Volunteers — slots only, no "VOLUNTEERS" header row. */}
          {useVolunteers && (
            <div className="cv3-volunteers">
              <div className="cv3-volunteers-row">
                <div className="cv3-volunteers-slot">
                  <small>WATER / GATORADE</small>
                  <b>{snacksVolunteer || "— TBD —"}</b>
                </div>
                <div className="cv3-volunteers-slot">
                  <small>BANANAS / PROTEIN BARS</small>
                  <b>{alcoholVolunteer || "— TBD —"}</b>
                </div>
              </div>
            </div>
          )}

          {/* Pitt Gabruz Cricket only — inline reporting-time footer
              sits inside the right column under volunteers, so the
              flyer drops the wide bottom band and fits on one screen
              without vertical scroll. */}
          {isPittGabruzCricket && (
            <div className="cv3-clockBand cv3-clockBand--inline">
              <Clock size={20} className="cv3-clockBand-icon"/>
              <div className="cv3-clockBand-text">
                <small>PLEASE PLAN TO BE AT THE GROUND</small>
                <b>NO LATER THAN {formatTimeTemplate(reportingTime)}</b>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Bottom reporting-time band — only for non-Pitt-Gabruz cricket
          teams (e.g. Rowdys), which still use the wide footer band. */}
      {!isPittGabruzCricket && (
        <div className="cv3-clockBand">
          <Clock size={28} className="cv3-clockBand-icon"/>
          <div className="cv3-clockBand-text">
            <small>PLEASE PLAN TO BE AT THE GROUND</small>
            <b>NO LATER THAN {formatTimeTemplate(reportingTime)}</b>
          </div>
        </div>
      )}

    </div>
  );
}
